import React, { useEffect, useState, useCallback, useRef } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Hexapi from "../utility/HexAPI/Hexapi";

const ReminderNotification = ({ open = false, AgentID, ReminderClick }) => {
  const [reminders, setReminders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const audioRef = useRef(null);
  const prevReminderCount = useRef(0);

  /* ---------------- FETCH REMINDERS ---------------- */
  const fetchReminders = async () => {
    if (!AgentID) return;

    try {
      const resp = await Hexapi({
        query: `[dbo].[HCMS_Proc_ReminderNotification_GetData] @AgentID='{0}'`,
        queryArr: [`${AgentID}`],
      });

      const newReminders = resp?.reminderlist || [];

      // 🔔 Play sound only when NEW reminder arrives
      if (
        prevReminderCount.current > 0 &&
        newReminders.length > prevReminderCount.current
      ) {
        audioRef.current?.play().catch(() => {});
      }

      prevReminderCount.current = newReminders.length;

      setReminders(newReminders);
      setCurrentIndex((prev) =>
        newReminders.length === 0
          ? 0
          : Math.min(prev, newReminders.length - 1)
      );
    } catch (err) {
      console.error("Fetch reminder error:", err);
    }
  };

  /* ---------------- ACTIONS ---------------- */
  const closeReminder = async (reminderId) => {
    try {
      await Hexapi({
        query: `[dbo].[HCMS_Proc_ReminderNotification_Close] @AgentID='{0}',@ReminderID='{1}'`,
        queryArr: [`${AgentID}`, `${reminderId}`],
      });

      await fetchReminders();
    } catch (err) {
      console.error("Close reminder error:", err);
    }
  };

  const snoozeReminder = async (reminderId) => {
    try {
      await Hexapi({
        query: `[dbo].[HCMS_Proc_ReminderNotification_Snooze] @AgentID='{0}',@ReminderID='{1}'`,
        queryArr: [`${AgentID}`, `${reminderId}`],
      });

      await fetchReminders();
    } catch (err) {
      console.error("Snooze reminder error:", err);
    }
  };

  /* ---------------- EFFECTS ---------------- */
  useEffect(() => {
    if (!open || !AgentID) return;

    fetchReminders();
    const intervalId = setInterval(fetchReminders, 60000);

    return () => clearInterval(intervalId);
  }, [open, AgentID]);

  useEffect(() => {
    audioRef.current = new Audio("/notification.mp3");
    audioRef.current.volume = 0.7;
  }, []);

  /* ---------------- KEYBOARD NAV ---------------- */
  const handleKey = useCallback(
    (e) => {
      if (!reminders.length) return;

      if (e.key === "ArrowRight") {
        setCurrentIndex((prev) =>
          prev === reminders.length - 1 ? 0 : prev + 1
        );
      }

      if (e.key === "ArrowLeft") {
        setCurrentIndex((prev) =>
          prev === 0 ? reminders.length - 1 : prev - 1
        );
      }
    },
    [reminders]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  /* ---------------- SAFETY GUARD ---------------- */
  if (!open || reminders.length === 0) return null;

  const reminder = reminders[currentIndex];
  if (!reminder) return null;

  /* ---------------- STYLES ---------------- */
  const styles = {
    wrapper: {
      position: "fixed",
      bottom: "20px",
      right: "20px",
      zIndex: 999999,
    },
    card: {
      width: "320px",
      background: "#fff",
      borderRadius: "10px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
      overflow: "hidden",
      fontFamily: "Arial",
      cursor: "pointer",
    },
    header: {
      padding: "10px",
      background: "#1250ac",
      color: "#fff",
      fontWeight: "bold",
    },
    body: {
      padding: "12px",
      fontSize: "14px",
    },
    footer: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      justifyContent: "space-between",
    },
    navBtn: {
      cursor: "pointer",
      color: "#1250ac",
    },
    btn: {
      background: "#1250ac",
      color: "#fff",
      border: "none",
      borderRadius: "4px",
      padding: "6px 10px",
      cursor: "pointer",
      fontSize: "12px",
    },
    count: {
      fontSize: "12px",
      color: "#555",
    },
  };

  return (
    <div style={styles.wrapper}>
      <div
        style={styles.card}
        onClick={() => ReminderClick?.(reminder.leadid)}
      >
        <div style={styles.header}>Follow Up Reminder</div>

        <div style={styles.body}>
          <div
            dangerouslySetInnerHTML={{ __html: reminder.description }}
          />
        </div>

        <div style={styles.footer}>
          <ChevronLeftIcon
            style={styles.navBtn}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex(
                currentIndex === 0
                  ? reminders.length - 1
                  : currentIndex - 1
              );
            }}
          />

          <span style={styles.count}>
            {currentIndex + 1} / {reminders.length}
          </span>

          <ChevronRightIcon
            style={styles.navBtn}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex(
                currentIndex === reminders.length - 1
                  ? 0
                  : currentIndex + 1
              );
            }}
          />

          <button
            style={styles.btn}
            onClick={(e) => {
              e.stopPropagation();
              snoozeReminder(reminder.reminderid);
            }}
          >
            Snooze
          </button>

          <button
            style={styles.btn}
            onClick={(e) => {
              e.stopPropagation();
              closeReminder(reminder.reminderid);
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReminderNotification;
